import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { StoreProvider } from './state/store'
import { ToastProvider } from './components/Toast'
import { Rail } from './components/Shell'
import { SoftKeyboard } from './components/SoftKeyboard'
import { Close } from './screens/Close'
import { TimeClock } from './screens/TimeClock'
import { Home } from './screens/Home'
import { Intake } from './screens/Intake'
import { IntakeReview } from './screens/IntakeReview'
import { IntakeRecorded } from './screens/IntakeRecorded'
import { Consignors } from './screens/Consignors'
import { Customers } from './screens/Customers'
import { Sell } from './screens/Sell'
import { SellPrice } from './screens/SellPrice'
import { SellPay } from './screens/SellPay'
import { SellReview } from './screens/SellReview'
import { SellDone } from './screens/SellDone'
import { SellCard } from './screens/SellCard'
import { SellCollect } from './screens/SellCollect'
import { Sales } from './screens/Sales'
import { Team } from './screens/Team'
import { Qa } from './screens/Qa'
import { QaCheck } from './screens/QaCheck'
import { QaLog } from './screens/QaLog'
import { Trade } from './screens/Trade'
import { TradePrice } from './screens/TradePrice'
import { TradeGive } from './screens/TradeGive'
import { TradeReview } from './screens/TradeReview'
import { TradeCard } from './screens/TradeCard'
import { TradeCollect } from './screens/TradeCollect'
import { TradeDone } from './screens/TradeDone'
import { IntakePending } from './screens/IntakePending'
import { IntakeRecent } from './screens/IntakeRecent'
import { PrintIntake } from './screens/Print'
import { Open } from './screens/Open'
import { Inbox } from './screens/Inbox'
import { ScreenBoundary } from './components/ScreenBoundary'
import { readDay, lockDay, touchDay, IDLE_MINUTES, type DayState } from './state/day'
import { hideSplash } from './lib/splash'
import { Stardust } from './components/Stardust'
import { clearCache } from './lib/cache'
import { isPhone } from './lib/device'
import { PhoneCtx, TabBar, More, tabBarShown } from './components/Phone'

function hasStoredSession() {
  try { return Object.keys(localStorage).some((k) => k.startsWith('sb-') && k.endsWith('-auth-token')) } catch { return false }
}

function Shell({ day, onLock }: { day: DayState; onLock: () => void }) {
  const { pathname } = useLocation()
  if (pathname.startsWith('/print/')) return <Routes><Route path="/print/intake/:id" element={<PrintIntake />} /></Routes>
  return (
    <StoreProvider>
      <ToastProvider>
      <PhoneCtx.Provider value={{ day, onLock }}>
      <div className={'app' + (isPhone && tabBarShown(pathname) ? ' has-tabbar' : '')}>
        {!isPhone && <Rail day={day} onLock={onLock} />}
        <ScreenBoundary key={pathname} path={pathname}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/sell/price" element={<SellPrice />} />
          <Route path="/sell/pay" element={<SellPay />} />
          <Route path="/sell/review" element={<SellReview />} />
          <Route path="/sell/done" element={<SellDone />} />
          <Route path="/sell/card" element={<SellCard />} />
          <Route path="/sell/collect/:id" element={<SellCollect />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/trade" element={<Trade />} />
          <Route path="/trade/price" element={<TradePrice />} />
          <Route path="/trade/give" element={<TradeGive />} />
          <Route path="/trade/review" element={<TradeReview />} />
          <Route path="/trade/card" element={<TradeCard />} />
          <Route path="/trade/collect/:id" element={<TradeCollect />} />
          <Route path="/trade/done" element={<TradeDone />} />
          <Route path="/intake/pending" element={<IntakePending />} />
          <Route path="/intake/recent" element={<IntakeRecent />} />
          <Route path="/intake" element={<Intake />} />
          <Route path="/intake/review" element={<IntakeReview />} />
          <Route path="/intake/recorded" element={<IntakeRecorded />} />
          <Route path="/qa" element={<Qa />} />
          <Route path="/qa/check" element={<QaCheck />} />
          <Route path="/qa/log" element={<QaLog />} />
          <Route path="/consignors" element={<Consignors />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/inbox/:kind/:id" element={<Inbox />} />
          <Route path="/team" element={<Team />} />
          <Route path="/close" element={<Close onLock={onLock} />} />
          <Route path="/time" element={<TimeClock onLock={onLock} />} />
          {isPhone && <Route path="/more" element={<More />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ScreenBoundary>
        {isPhone ? <TabBar /> : <SoftKeyboard />}
      </div>
      </PhoneCtx.Provider>
      </ToastProvider>
    </StoreProvider>
  )
}

export default function App() {
  // Boot (D2): with a stored session on the device, draw the app at once instead of waiting for the session to be read back
  // (and, after an hour, refreshed over the network). Every request still waits for a valid session inside supabase-js, and
  // if there turns out to be none the auth listener below switches to the pairing screen.
  const [session, setSession] = useState<boolean | null>(() => hasStoredSession() ? true : null)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(!!data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((e, s) => { if (e === 'SIGNED_OUT') clearCache(); setSession(!!s) })
    return () => sub.subscription.unsubscribe()
  }, [])
  const [day, setDay] = useState<DayState | null>(() => readDay())
  useEffect(() => {
    // re-check the day whenever the app comes back to the front (new day, left open overnight, or sat idle)
    const check = () => { if (document.visibilityState === 'visible') setDay(readDay()) }
    document.addEventListener('visibilitychange', check); return () => document.removeEventListener('visibilitychange', check)
  }, [])
  useEffect(() => {
    // idle lock: any touch or key resets the clock; after IDLE_MINUTES without one the app locks and asks for an ID
    if (!day) return
    let t: number | undefined
    const arm = () => { touchDay(); window.clearTimeout(t); t = window.setTimeout(() => setDay(readDay()), IDLE_MINUTES * 60_000 + 500) }
    const evs: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'scroll']
    evs.forEach((e) => window.addEventListener(e, arm, { passive: true }))
    arm()
    return () => { window.clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, arm)) }
  }, [day])
  useEffect(() => { if (session !== null) hideSplash() }, [session])   // the launch splash (index.html) goes once there is a screen to show
  // Motion: the welcome after every sign-in (Tim, 29 Sep: the full one every time, after the idle lock too). The star forms from
  // its speckles, one glint, the greeting. The Shell renders behind it, so Home and its prefetches are ready when it ends; a tap
  // ends it at once. Home hears 'starchive:welcomed' and brings its tiles in then.
  const [welcome, setWelcome] = useState<{ name: string } | null>(null)
  const opened = (d: DayState) => { setWelcome({ name: d.name.split(' ')[0] || d.name }); setDay(d) }
  const welcomed = () => { setWelcome(null); window.dispatchEvent(new Event('starchive:welcomed')) }
  const isPrint = window.location.pathname.startsWith('/print/')
  if (session === null) return null
  // no pairing screen: a device with no session signs in with an employee ID on the same pad (Open checks it on the server)
  if (!session || (!day && !isPrint)) return <Open signedIn={!!session} onOpen={opened} />
  return (
    <>
      <BrowserRouter useTransitions={false}><Shell day={day ?? { employee_id: '', name: '', date: '', openedAt: 0 }} onLock={async () => { setWelcome(null); await lockDay(day); setDay(null) }} /></BrowserRouter>
      {welcome && !isPrint && <Stardust name={welcome.name} onDone={welcomed} />}
    </>
  )
}
